import React, { useState } from 'react';
import { Box, Typography, Button, Container, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import documentIcon from '../assets/icons/iconsax-document-text-2.svg';
import likeIcon from '../assets/icons/like.svg';
import Footer from '../components/Footer';
import AuthModal from '../components/auth/AuthModal';
import { 
  AutoFixHigh, 
  Speed, 
  Security, 
  CheckCircle, 
  Assignment,
  Description,
  BusinessCenter
} from '@mui/icons-material';
import { PAY_PER_USE_PRICING, CREDIT_VALUE_CZK } from '../config/pricing';

const DocumentsInfoPage: React.FC = () => {
  const navigate = useNavigate();
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>('login');
  const { isAuthenticated } = useAuth();
  const documentsPricing = PAY_PER_USE_PRICING.find(item => item.key === 'document-generation');
  const creditValue = CREDIT_VALUE_CZK.toLocaleString('cs-CZ', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const handleGetStarted = () => {
    setAuthModalMode('register');
    setAuthModalOpen(true);
  };

  const stats = [
    { value: '20+', label: 'Typů dokumentů', icon: <Description /> },
    { value: '90%', label: 'Úspora času', icon: <Speed /> },
    { value: 'PDF', label: 'Export formát', icon: <Assignment /> },
    { value: '24/7', label: 'Dostupnost', icon: <Security /> }
  ];

  const features = [
    {
      icon: <AutoFixHigh sx={{ fontSize: 32 }} />,
      title: 'AI Asistent',
      description: 'Inteligentní asistent vás provede celým procesem vytváření dokumentu krok za krokem.',
      benefit: 'Jednoduché vyplnění'
    },
    {
      icon: <Speed sx={{ fontSize: 32 }} />,
      title: 'Rychlé Generování',
      description: 'Vytvořte profesionální právní dokument během několika minut místo hodin.',
      benefit: 'Úspora času'
    },
    {
      icon: <Security sx={{ fontSize: 32 }} />,
      title: 'Právní Validita',
      description: 'Všechny dokumenty jsou vytvořené podle platného českého práva a průběžně aktualizované.',
      benefit: 'Ověřená kvalita'
    },
    {
      icon: <CheckCircle sx={{ fontSize: 32 }} />,
      title: 'Přizpůsobení',
      description: 'Každý dokument se automaticky přizpůsobí vašim konkrétním potřebám a situaci.',
      benefit: 'Personalizace'
    }
  ];

  const documentTypes = [
    {
      title: 'Smlouvy',
      icon: <BusinessCenter sx={{ fontSize: 48 }} />,
      description: 'Nájemní smlouvy, kupní smlouvy, smlouvy o dílo a další obchodní dokumenty.',
      templates: ['Nájemní smlouva', 'Kupní smlouva', 'Smlouva o dílo', 'Pracovní smlouva'],
      color: '#E8F5E8'
    },
    {
      title: 'Právní Písemnosti',
      icon: <Description sx={{ fontSize: 48 }} />,
      description: 'Stížnosti, odpovědi na výzvy, plné moci a další právní dokumenty.',
      templates: ['Stížnost', 'Odpověď na výzvu', 'Plná moc', 'Prohlášení'],
      color: '#FFF3E0'
    },
    {
      title: 'Firemní Dokumenty',
      icon: <Assignment sx={{ fontSize: 48 }} />,
      description: 'Obchodní podmínky, smlouvy o mlčenlivosti a další podnikové dokumenty.',
      templates: ['Obchodní podmínky', 'NDA', 'Partnerská smlouva'],
      color: '#E3F2FD'
    }
  ];

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh',
      backgroundColor: '#F2F2F2',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <Container maxWidth={false} sx={{ 
        maxWidth: '1920px',
        px: { xs: 2, sm: 3, md: 4, lg: '277px' }
      }}>
        {/* Hero Section */}
        <Box sx={{
          position: 'relative',
          mt: { xs: '100px', sm: '120px', md: '132px' },
          mb: { xs: 8, sm: 10, md: 12 }
        }}>
          {/* Enhanced Gradient Background */}
          <Box sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '400%', sm: '250%', md: '180%', lg: '1600px' },
            height: { xs: '400px', sm: '450px', md: '500px' },
            borderRadius: '50%',
            background: 'radial-gradient(circle at 50% 50%, rgba(100, 100, 255, 0.1) 0%, rgba(255, 100, 100, 0.05) 30%, rgba(128, 128, 128, 0.3) 60%, rgba(237, 237, 237, 0) 100%)',
            filter: 'blur(120px)',
            zIndex: 0
          }} />
          
          <Box sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: 3, sm: 4, md: 5 },
            py: { xs: 6, sm: 7, md: 8 },
            zIndex: 1
          }}>

            <Box 
              component="img" 
              src={documentIcon} 
              alt="Generování dokumentů" 
              sx={{ 
                width: { xs: '80px', sm: '100px', md: '120px' },
                height: { xs: '80px', sm: '100px', md: '120px' },
                filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.1))'
              }} 
            />
            
            <Typography sx={{ 
              fontFamily: 'Inter',
              fontWeight: 800,
              fontSize: { xs: '32px', sm: '42px', md: '52px', lg: '64px' },
              lineHeight: { xs: 1.1, md: 1.1 },
              textAlign: 'center',
              color: '#000000',
              maxWidth: { xs: '100%', sm: '700px', md: '900px', lg: '1100px' },
              background: 'linear-gradient(135deg, #1F1F1F 0%, #666666 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              px: { xs: 2, sm: 3 }
            }}>
              Profesionální dokumenty za 2 minuty místo{' '}
              <Box component="span" sx={{ 
                background: 'linear-gradient(135deg, #e72f0d 0%, #e72f0d 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                2 týdnů
              </Box>
            </Typography>

            <Typography sx={{ 
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: { xs: '16px', sm: '18px', md: '20px' },
              lineHeight: 1.5,
              textAlign: 'center',
              color: '#666666',
              maxWidth: '600px',
              px: { xs: 2, sm: 3 }
            }}>
              Ušetřete 95% času na tvorbu dokumentů. <strong>Kvalita advokáta za zlomek ceny - bez čekání, bez vysokých honorářů.</strong>
            </Typography>
            {documentsPricing && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: '16px',
                px: { xs: 2.5, sm: 3 },
                py: { xs: 1.25, sm: 1.5 },
                boxShadow: '0 10px 28px rgba(0,0,0,0.08)'
              }}>
                <Chip
                  label={`${documentsPricing.priceCZK.toLocaleString('cs-CZ')} Kč`}
                  sx={{ backgroundColor: '#1F1F1F', color: '#FFFFFF', fontWeight: 600 }}
                />
                <Chip
                  label={`${documentsPricing.credits.toLocaleString('cs-CZ')} kreditů`}
                  sx={{ backgroundColor: 'rgba(231,47,13,0.12)', color: '#e72f0d', fontWeight: 600 }}
                />
                <Typography sx={{ fontSize: '13px', color: '#424242', fontWeight: 500 }}>
                  1 kredit = {creditValue} Kč. Platba se odečte až po doručení dokumentu.
                </Typography>
              </Box>
            )}
            
            <Box sx={{ 
              display: 'flex',
              gap: { xs: 2, sm: 3 },
              flexWrap: 'wrap',
              justifyContent: 'center',
              mt: 2
            }}>
              <Button
                onClick={() => {
                  const intended = '/dashboard?open=documents';
                  try { sessionStorage.setItem('intendedPath', intended); } catch {}
                  if (isAuthenticated) {
                    navigate(intended);
                  } else {
                    navigate(`/dashboard?requireRegistration=1&authMode=login&intendedPath=${encodeURIComponent(intended)}`);
                  }
                }}
                size="large"
                sx={{
                  padding: { xs: '14px 28px', sm: '16px 32px' },
                  bgcolor: '#1F1F1F !important',
                  backgroundColor: '#1F1F1F !important',
                  background: '#1F1F1F !important',
                  color: '#FFFFFF !important',
                  fontSize: { xs: '14px', sm: '16px' },
                  fontWeight: 600,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  border: 'none !important',
                  boxShadow: '0 4px 12px rgba(31, 31, 31, 0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: '#e72f0d !important',
                    backgroundColor: '#e72f0d !important',
                    background: '#e72f0d !important',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(231, 47, 13, 0.25)'
                  }
                }}
              >
                Vytvořit dokument
              </Button>
              
            </Box>

            {/* Jednoduchý návod – Generování dokumentů */}
            <Box sx={{
              mt: { xs: 3, sm: 4 },
              p: { xs: 2.5, sm: 3 },
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
              border: '1px solid rgba(0,0,0,0.06)',
              maxWidth: 900,
              mx: 'auto'
            }}>
              <Typography sx={{ fontFamily: 'Inter', fontWeight: 700, fontSize: { xs: '20px', sm: '22px' }, color: '#1F1F1F', textAlign: 'center', mb: 1.5 }}>
                Jak začít.
              </Typography>
              <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: { xs: '14px', sm: '15px' }, color: '#666', textAlign: 'center', mb: 2.5 }}>
                Po kliknutí na „Začít“ budete přesměrováni na Dashboard. Tam proběhne přihlášení/registrace a odkud ovládáte všechny funkce.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '15px', sm: '16px' } }}>1) Vyberte dokument</Typography>
                    <Typography sx={{ color: '#555', fontSize: { xs: '14px', sm: '15px' } }}>V Dashboardu otevřete „Generování dokumentů“ a zvolte šablonu (např. Kupní/Nájemní smlouva).</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '15px', sm: '16px' } }}>2) Vyplňte formulář</Typography>
                    <Typography sx={{ color: '#555', fontSize: { xs: '14px', sm: '15px' } }}>Stačí odpovědět na jednoduché otázky. Náhled uvidíte hned.</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '15px', sm: '16px' } }}>3) Stáhněte hotové PDF</Typography>
                    <Typography sx={{ color: '#555', fontSize: { xs: '14px', sm: '15px' } }}>Dokument si stáhnete jako PDF a můžete ho rovnou použít.</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '15px', sm: '16px' } }}>4) Vše je uložené</Typography>
                    <Typography sx={{ color: '#555', fontSize: { xs: '14px', sm: '15px' } }}>Poslední dokumenty najdete vždy v Dashboardu a můžete je kdykoli upravit.</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

          </Box>
        </Box>

        {/* Stats Section */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: { xs: 2, sm: 3 },
          mb: { xs: 8, sm: 10, md: 12 }
        }}>
          {stats.map((stat, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                padding: { xs: '24px 16px', sm: '32px 20px' },
                backgroundColor: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                transition: 'all 0.5s ease-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                }
              }}
            >
              <Box sx={{ color: '#1F1F1F', mb: 1 }}>
                {stat.icon}
              </Box>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: { xs: '24px', sm: '28px' },
                color: '#1F1F1F'
              }}>
                {stat.value}
              </Typography>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: { xs: '12px', sm: '14px' },
                color: '#666666',
                textAlign: 'center'
              }}>
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Features Section */}
        <Box sx={{
          position: 'relative',
          mb: { xs: 8, sm: 10, md: 12 }
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: 4, sm: 5, md: 6 },
            mb: { xs: 4, sm: 6 }
          }}>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 700,
              fontSize: { xs: '28px', sm: '36px', md: '42px' },
              lineHeight: 1.2,
              textAlign: 'center',
              color: '#1F1F1F',
              maxWidth: '800px'
            }}>
              Proč používat náš AI generátor?
            </Typography>
            
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: { xs: 3, sm: 4 },
              width: '100%'
            }}>
              {features.map((feature, index) => (
                <Box
                  key={index}
                  onMouseEnter={() => setHoveredFeature(index)}
                  onMouseLeave={() => setHoveredFeature(null)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    padding: { xs: '32px 24px', sm: '40px 32px' },
                    backgroundColor: hoveredFeature === index 
                      ? 'rgba(255,255,255,1)' 
                      : 'rgba(255,255,255,0.9)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '24px',
                    boxShadow: hoveredFeature === index 
                      ? '0 12px 40px rgba(0,0,0,0.15)' 
                      : '0 4px 20px rgba(0,0,0,0.08)',
                    transition: 'all 0.5s ease-out',
                    cursor: 'pointer',
                    transform: hoveredFeature === index ? 'translateY(-8px)' : 'translateY(0)',
                    border: hoveredFeature === index ? '2px solid rgba(231,47,13,0.2)' : '2px solid transparent'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      color: hoveredFeature === index ? '#e72f0d' : '#1F1F1F',
                      transition: 'color 0.4s ease-out'
                    }}>
                      {feature.icon}
                    </Box>
                    <Chip 
                      label={feature.benefit}
                      size="small"
                      sx={{ 
                        backgroundColor: hoveredFeature === index ? '#e72f0d' : '#E8F5E8',
                        color: hoveredFeature === index ? 'white' : '#2E7D32',
                        fontWeight: 600,
                        fontSize: '11px'
                      }}
                    />
                  </Box>
                  
                  <Box>
                    <Typography sx={{
                      fontFamily: 'Inter',
                      fontWeight: 600,
                      fontSize: { xs: '20px', sm: '22px' },
                      color: '#1F1F1F',
                      mb: 1
                    }}>
                      {feature.title}
                    </Typography>
                    <Typography sx={{
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      fontSize: { xs: '14px', sm: '15px' },
                      lineHeight: 1.6,
                      color: '#666666'
                    }}>
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {documentsPricing && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  borderRadius: '16px',
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1.5, sm: 2 },
                  boxShadow: '0 12px 32px rgba(0,0,0,0.08)'
                }}
              >
                <Chip
                  label={`${documentsPricing.priceCZK.toLocaleString('cs-CZ')} Kč`}
                  sx={{ backgroundColor: '#1F1F1F', color: '#FFFFFF', fontWeight: 600 }}
                />
                <Chip
                  label={`${documentsPricing.credits.toLocaleString('cs-CZ')} kreditů`}
                  sx={{ backgroundColor: 'rgba(231,47,13,0.12)', color: '#e72f0d', fontWeight: 600 }}
                />
                <Typography sx={{ fontFamily: 'Inter', fontSize: '13px', color: '#424242', fontWeight: 500 }}>
                  1 kredit = {creditValue} Kč. Platba se odečte až po stažení finálního dokumentu.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Document Types Grid */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(3, 1fr)' 
          },
          gap: { xs: 3, sm: 4 },
          mb: { xs: 8, sm: 10, md: 12 }
        }}>
          {documentTypes.map((docType, index) => (
            <Box 
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: { xs: 3, sm: 4 },
                padding: { xs: '32px 24px', sm: '40px 32px' },
                backgroundColor: '#FFFFFF',
                borderRadius: '24px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.5s ease-out',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)'
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ color: '#1F1F1F' }}>
                  {docType.icon}
                </Box>
                <Typography sx={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: { xs: '22px', sm: '24px' },
                  lineHeight: 1.3,
                  color: '#1F1F1F'
                }}>
                  {docType.title}
                </Typography>
                <Typography sx={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: { xs: '14px', sm: '15px' },
                  lineHeight: 1.5,
                  color: '#666666'
                }}>
                  {docType.description}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {docType.templates.map((template, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircle sx={{ fontSize: 16, color: '#4CAF50', mr: 1 }} />
                      <Typography sx={{
                        fontFamily: 'Inter',
                        fontWeight: 400,
                        fontSize: '13px',
                        color: '#666666'
                      }}>
                        {template}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Chip 
                  label="AI generování" 
                  size="small" 
                  sx={{ 
                    alignSelf: 'flex-start',
                    backgroundColor: docType.color,
                    color: '#1F1F1F',
                    fontWeight: 600
                  }}
                />
              </Box>
              <Button
                onClick={() => {
                  const intended = '/dashboard?open=documents';
                  try { sessionStorage.setItem('intendedPath', intended); } catch {}
                  if (isAuthenticated) {
                    navigate(intended);
                  } else {
                    navigate(`/dashboard?requireRegistration=1&authMode=login&intendedPath=${encodeURIComponent(intended)}`);
                  }
                }}
                variant="outlined"
                sx={{
                  width: '100%',
                  padding: { xs: '14px 16px', sm: '16px 20px' },
                  color: '#1F1F1F',
                  borderColor: '#1F1F1F',
                  fontSize: { xs: '14px', sm: '15px' },
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(31, 31, 31, 0.08)',
                    borderColor: '#1F1F1F'
                  }
                }}
              >
                Vytvořit dokument
              </Button>
            </Box>
          ))}
        </Box>

        {/* Professional Warning */}
        <Box sx={{
          position: 'relative',
          mb: { xs: 6, sm: 8, md: 10 }
        }}>
          <Box sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            padding: { xs: '16px', sm: '20px' },
            backgroundColor: '#4C4C4C',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            zIndex: 1
          }}>
            <Box 
              component="img" 
              src={likeIcon} 
              alt="Like" 
              sx={{ 
                width: { xs: '20px', sm: '24px' },
                height: { xs: '20px', sm: '24px' }
              }} 
            />
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 500,
              fontSize: { xs: '12px', sm: '14px' },
              lineHeight: 1.5,
              textAlign: 'center',
              color: '#DCDCDC'
            }}>
              Profesionální upozornění: AI generátor vytváří přizpůsobené právní dokumenty. Pro závažné právní záležitosti konzultujte kvalifikovaného advokáta.
            </Typography>
          </Box>
        </Box>

        {/* Final CTA */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: 4, sm: 5 },
          padding: { xs: '48px 32px', sm: '64px 40px' },
          background: 'linear-gradient(135deg, #1F1F1F 0%, #333333 100%)',
          borderRadius: '24px',
          boxShadow: '0px 20px 60px rgba(0, 0, 0, 0.25)',
          mb: { xs: 6, sm: 8, md: 10 }
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 700,
              fontSize: { xs: '32px', sm: '42px', md: '48px' },
              lineHeight: 1.1,
              color: '#FFFFFF',
              mb: 3
            }}>
              Připraveni ušetřit tisíce na <br />
              advokátních honorářích?
            </Typography>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: { xs: '16px', sm: '18px' },
              lineHeight: 1.5,
              color: '#B0B0B0',
              maxWidth: '600px'
            }}>
              Začněte vytvářet profesionální právní dokumenty během několika minut s pomocí našeho AI asistenta
            </Typography>
          </Box>
          
          <Box sx={{
            display: 'flex',
            gap: { xs: 2, sm: 3 },
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            <Button 
              onClick={() => {
                const intended = '/dashboard?open=documents';
                try { sessionStorage.setItem('intendedPath', intended); } catch {}
                if (isAuthenticated) {
                  navigate(intended);
                } else {
                  navigate(`/dashboard?requireRegistration=1&authMode=login&intendedPath=${encodeURIComponent(intended)}`);
                }
              }}
              size="large"
              sx={{
                padding: { xs: '16px 32px', sm: '20px 40px' },
                backgroundColor: '#FFFFFF',
                color: '#1F1F1F',
                borderRadius: '12px',
                fontSize: { xs: '16px', sm: '18px' },
                fontWeight: 700,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                boxShadow: '0 8px 32px rgba(255,255,255,0.2)',
                '&:hover': { 
                  backgroundColor: '#F0F0F0',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(255,255,255,0.3)'
                }
              }}
            >
              Vytvořit dokument
            </Button>
            <Button 
              onClick={() => navigate('/')}
              variant="outlined"
              sx={{
                padding: { xs: '16px 32px', sm: '20px 40px' },
                border: '2px solid #666666',
                color: '#FFFFFF',
                backgroundColor: 'transparent',
                borderRadius: '12px',
                fontSize: { xs: '16px', sm: '18px' },
                fontWeight: 600,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: '#FFFFFF'
                }
              }}
            >
              Zobrazit všechny služby
            </Button>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            opacity: 0.8
          }}>
            <Typography sx={{ 
              fontSize: '14px', 
              color: '#B0B0B0',
              fontWeight: 500
            }}>
              • Bez registrace  • Bez závazků  • Okamžitý přístup
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* Footer */}
      <Footer />

      {/* Auth Modal */}
      <AuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialMode={authModalMode}
      />
    </Box>
  );
};

export default DocumentsInfoPage;
